/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useMemo } from 'react';

import { AlwaysLightMode, breakpoints } from '@coursera/cds-core';

import AdobeLogoSvg from 'bundles/partners-graphql/components/listing/cds/AdobeLogoSvg';
import GoogleLogoSvg from 'bundles/partners-graphql/components/listing/cds/GoogleLogoSvg';
import IbmLogoSvg from 'bundles/partners-graphql/components/listing/cds/IbmLogoSvg';
import MetaLogoSvg from 'bundles/partners-graphql/components/listing/cds/MetaLogoSvg';
import MicrosoftLogoSvg from 'bundles/partners-graphql/components/listing/cds/MicrosoftLogoSvg';
import type { PartnerListingPagePartnerFragment as PartnerListingPagePartner } from 'bundles/partners-graphql/queries/__generated__/GetAllPartners';

const FEATURED_PARTNER_SLUGS = ['google', 'microsoft', 'adobe', 'meta', 'ibm-skills-network'];

type Props = {
  partners: PartnerListingPagePartner[];
};

const bubbleBase = css`
  position: absolute;
  border-radius: 50%;
  background: var(--cds-color-neutral-background-primary);
  border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const styles = {
  container: css`
    position: relative;
    width: 100%;
    height: 100%;
    pointer-events: none;

    ${breakpoints.down('xs')} {
      width: 140px;
      margin-left: auto;
    }
  `,
  logo: css`
    width: 60%;
    height: 60%;
    object-fit: contain;
  `,
  // google
  bubble0: css`
    ${bubbleBase}
    width: 160px;
    height: 160px;
    top: 20px;
    left: 50%;

    ${breakpoints.down('md')} {
      width: 136px;
      height: 136px;
      top: 32px;
      left: calc(50% + 12px);
    }

    ${breakpoints.down('sm')} {
      width: 98px;
      height: 98px;
      top: 42px;
      left: calc(50% + 22px);
    }

    ${breakpoints.down('xs')} {
      width: 84px;
      height: 84px;
      top: -4px;
      left: 76px;
    }
  `,
  // microsoft
  bubble1: css`
    ${bubbleBase}
    width: 140px;
    height: 140px;
    top: 80px;
    left: 26%;

    ${breakpoints.down('md')} {
      width: 116px;
      height: 116px;
      top: 92px;
      left: calc(26% + 12px);
    }

    ${breakpoints.down('sm')} {
      width: 88px;
      height: 88px;
      top: 102px;
      left: calc(26% + 22px);
    }

    ${breakpoints.down('xs')} {
      width: 74px;
      height: 74px;
      top: 90px;
      left: 88px;
    }
  `,
  // adobe
  bubble2: css`
    ${bubbleBase}
    width: 104px;
    height: 104px;
    top: -10px;
    left: 12%;

    ${breakpoints.down('md')} {
      width: 80px;
      height: 80px;
      top: 2px;
      left: calc(12% + 12px);
    }

    ${breakpoints.down('sm')} {
      width: 68px;
      height: 68px;
      top: 12px;
      left: calc(12% + 22px);
    }

    ${breakpoints.down('xs')} {
      width: 54px;
      height: 54px;
      top: 36px;
      left: 10px;
    }
  `,
  // meta
  bubble3: css`
    ${bubbleBase}
    width: 88px;
    height: 88px;
    top: 100px;
    left: 77%;

    ${breakpoints.down('md')} {
      width: 64px;
      height: 64px;
      top: 112px;
      left: calc(77% + 12px);
    }

    ${breakpoints.down('sm')} {
      width: 60px;
      height: 60px;
      top: 122px;
      left: calc(77% + 22px);
    }

    ${breakpoints.down('xs')} {
      width: 46px;
      height: 46px;
      top: 96px;
      left: 36px;
    }
  `,
  // ibm
  bubble4: css`
    ${bubbleBase}
    width: 96px;
    height: 96px;
    top: 12px;
    left: 90%;

    ${breakpoints.down('md')} {
      width: 72px;
      height: 72px;
      top: 24px;
      left: calc(90% + 12px);
    }

    ${breakpoints.down('sm')} {
      width: 60px;
      height: 60px;
      top: 34px;
      left: calc(90% + 22px);
    }

    ${breakpoints.down('xs')} {
      width: 46px;
      height: 46px;
      display: none;
    }
  `,
};

const bubbleStyles = [styles.bubble0, styles.bubble1, styles.bubble2, styles.bubble3, styles.bubble4];

const HeroPartnerLogos = ({ partners }: Props) => {
  const featuredPartners = useMemo(() => {
    const partnersBySlug = new Map(partners.map((p) => [p.slug, p]));
    return FEATURED_PARTNER_SLUGS.map((slug) => partnersBySlug.get(slug)).filter(
      (p): p is PartnerListingPagePartner => !!p?.squareLogo
    );
  }, [partners]);

  if (featuredPartners.length === 0) {
    return null;
  }

  return (
    <AlwaysLightMode>
      <div css={styles.container} aria-hidden="true">
        {featuredPartners.map((partner, index) => {
          const bubbleCss = bubbleStyles[index];
          if (!bubbleCss) return null;

          const svgLogos: Record<string, JSX.Element> = {
            adobe: <AdobeLogoSvg css={styles.logo} />,
            google: <GoogleLogoSvg css={styles.logo} />,
            'ibm-skills-network': <IbmLogoSvg css={styles.logo} />,
            meta: <MetaLogoSvg css={styles.logo} />,
            microsoft: <MicrosoftLogoSvg css={styles.logo} />,
          };

          const logoContent = svgLogos[partner.slug ?? ''];
          if (!logoContent) return null;

          return (
            <div key={partner.id} css={bubbleCss}>
              {logoContent}
            </div>
          );
        })}
      </div>
    </AlwaysLightMode>
  );
};

export default HeroPartnerLogos;
